import { useEffect, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button, Field, Input, Select, useToast } from '@w7/ui'
import { cmApi, type CmSport, type EventSearchHit } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import { parseEventIds } from '@/lib/parseEventIds'
import { FetchResultPanel, type CmFetchEnvelope } from '@/features/fetch/FetchResultPanel'
import { toFetchEnvelope } from '@/features/fetch/fetchEnvelope'
import { CmPanel, SPORTS } from './CmPanel'
import { CatalogSection, CatalogWorkflow } from './catalogGuide'

type Action = 'events' | 'markets' | 'cycle' | 'enable' | 'rates' | 'search'

function sportFromHint(hint: string | null): CmSport | null {
  if (!hint) return null
  const lower = hint.toLowerCase()
  if (lower.includes('soccer') || lower.includes('football')) return 'soccer'
  if (lower.includes('tennis')) return 'tennis'
  if (lower.includes('cricket')) return 'cricket'
  return null
}

export function ManualCatalogPage() {
  const toast = useToast()
  const [params] = useSearchParams()
  const [sport, setSport] = useState<CmSport>('soccer')
  const [eventIdsRaw, setEventIdsRaw] = useState('')
  const [searchQ, setSearchQ] = useState('')
  const [horizonHours, setHorizonHours] = useState('24')
  const [envelope, setEnvelope] = useState<CmFetchEnvelope | null>(null)
  const [lastAction, setLastAction] = useState<Action | null>(null)
  const [hits, setHits] = useState<EventSearchHit[]>([])

  useEffect(() => {
    const eventId = params.get('eventId')
    if (eventId) setEventIdsRaw(eventId)
    const hinted = sportFromHint(params.get('sportHint'))
    if (hinted) setSport(hinted)
  }, [params])

  const run = useMutation({
    mutationFn: async (action: Action) => {
      setLastAction(action)
      const headers = authHeaders()
      if (action === 'search') return cmApi.eventSearch(headers, searchQ.trim(), 25)
      if (action === 'events') return cmApi.fetchEvents(headers, sport)
      if (action === 'markets') {
        const eventIds = parseEventIds(eventIdsRaw)
        return cmApi.fetchMarkets(headers, sport, {
          eventIds: eventIds.length ? eventIds : undefined,
        })
      }
      if (action === 'cycle') return cmApi.runCycle(headers, sport)
      if (action === 'enable') {
        const hrs = Number(horizonHours)
        return cmApi.enableMarkets(headers, sport, Number.isFinite(hrs) ? hrs : undefined)
      }
      return cmApi.storeRates(headers)
    },
    onSuccess: (data, action) => {
      if (action === 'search') {
        const events = (data as { events?: EventSearchHit[] }).events ?? []
        setHits(events)
        setEnvelope(null)
        toast.success(events.length ? `Found ${events.length} event(s)` : 'No events matched')
        return
      }
      setEnvelope(toFetchEnvelope(data, sport))
      toast.success(
        action === 'events'
          ? 'Events fetch completed — next: fetch markets for selected BF ids'
          : action === 'markets'
            ? 'Markets fetch completed — next: enable or set competition automation'
            : action === 'cycle'
              ? 'Run cycle completed'
              : action === 'enable'
                ? 'Enable completed'
                : 'Rates store completed',
      )
    },
    onError: (err: Error) => {
      setEnvelope(null)
      toast.error(err.message)
    },
  })

  const onAction = (action: Action) => (e: FormEvent) => {
    e.preventDefault()
    run.mutate(action)
  }

  return (
    <CmPanel
      wide
      title="Manual catalog"
      subtitle="Step-by-step: find events → fetch markets → enable for the player portal"
    >
      <div className="cm-catalog-card">
        <CatalogWorkflow
          steps={[
            {
              title: 'Find events',
              body: 'Search by name/Betfair id, or fetch all event shells for a sport.',
            },
            {
              title: 'Fetch markets',
              body: 'Paste Betfair event ids (uncapped for CM) or leave empty to discover like the worker.',
            },
            {
              title: 'Enable',
              body: (
                <>
                  Turn on markets within the hours window below, or use{' '}
                  <Link to="/catalog/automation">Automation</Link> for scheduled fetch.
                </>
              ),
            },
          ]}
        />
      </div>

      <div className="cm-manual-grid">
      <CatalogSection
        title="Search catalog"
        description="Find an event already on the portal, then click a result to fill Betfair event ids below."
      >
        <form className="cm-form" onSubmit={onAction('search')}>
          <Field
            label="Event name or Betfair id"
            hint="Partial names work. Click a result to select its Betfair event id."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                value={searchQ}
                onChange={(ev) => setSearchQ(ev.target.value)}
                placeholder="Arsenal or 34223314"
                data-testid="manual-event-search"
              />
            )}
          </Field>
          <div className="cm-actions">
            <Button
              type="submit"
              variant="secondary"
              busy={run.isPending && lastAction === 'search'}
              disabled={run.isPending}
            >
              Search
            </Button>
          </div>
        </form>

        {hits.length ? (
          <div className="cm-search-results" data-testid="manual-search-results">
            {hits.map((hit) => (
              <button
                key={hit.eventId}
                type="button"
                className="cm-search-row"
                onClick={() => {
                  setEventIdsRaw(hit.vendorEventId)
                  const hinted = sportFromHint(hit.sportName)
                  if (hinted) setSport(hinted)
                  toast.success(`Selected Betfair ${hit.vendorEventId}`)
                }}
              >
                <strong>{hit.name}</strong>
                <span>
                  {hit.sportName}
                  {hit.leagueName ? ` · ${hit.leagueName}` : ''} · Betfair {hit.vendorEventId}
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </CatalogSection>

      <CatalogSection
        title="Fetch & enable"
        description="Run these in order for a sport. Enable uses the horizon hours field."
      >
        <form className="cm-form" onSubmit={onAction('markets')}>
          <div className="cm-form-grid">
            <Field label="Sport" required hint="Soccer, tennis, or cricket.">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={sport}
                  onChange={(ev) => setSport(ev.target.value as CmSport)}
                  options={[...SPORTS]}
                />
              )}
            </Field>
            <Field
              label="Betfair event ids"
              hint="Optional. Comma-separated ids — CM explicit fetch is uncapped. Empty = discover mode."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={eventIdsRaw}
                  onChange={(ev) => setEventIdsRaw(ev.target.value)}
                  placeholder="e.g. 34223314"
                  data-testid="manual-event-ids"
                />
              )}
            </Field>
            <Field
              label="Enable window (hours)"
              hint="Step 3 only: enable markets for matches starting within this many hours from now. Example: 24 = next 24 hours."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={horizonHours}
                  onChange={(ev) => setHorizonHours(ev.target.value)}
                  inputMode="numeric"
                />
              )}
            </Field>
          </div>

          <div className="cm-action-group">
            <p className="cm-action-group-label">Primary flow</p>
            <div className="cm-actions">
              <Button
                type="button"
                variant="secondary"
                busy={run.isPending && lastAction === 'events'}
                disabled={run.isPending}
                onClick={() => run.mutate('events')}
              >
                1. Fetch events
              </Button>
              <Button
                type="submit"
                variant="primary"
                busy={run.isPending && lastAction === 'markets'}
                disabled={run.isPending}
              >
                2. Fetch markets
              </Button>
              <Button
                type="button"
                variant="secondary"
                busy={run.isPending && lastAction === 'enable'}
                disabled={run.isPending}
                onClick={() => run.mutate('enable')}
              >
                3. Enable markets
              </Button>
            </div>
          </div>

          <div className="cm-action-group">
            <p className="cm-action-group-label">Advanced</p>
            <div className="cm-actions">
              <Button
                type="button"
                variant="primary"
                busy={run.isPending && lastAction === 'cycle'}
                disabled={run.isPending}
                onClick={() => run.mutate('cycle')}
              >
                Run cycle
              </Button>
              <Button
                type="button"
                variant="ghost"
                busy={run.isPending && lastAction === 'rates'}
                disabled={run.isPending}
                onClick={() => run.mutate('rates')}
              >
                Store rates
              </Button>
            </div>
            <p className="cm-hint">
              Run cycle = full fetch + enable in one shot. Store rates = push current exchange rates to
              Redis (usually automatic via cron).
            </p>
          </div>
        </form>
      </CatalogSection>
      </div>
      <div className="cm-catalog-card">
        <FetchResultPanel data={envelope} />
      </div>
    </CmPanel>
  )
}
